// Common validation functions for the entire application

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface FieldValidation {
  [key: string]: string | null;
}

export const EMAIL_PATTERN =
  /^[a-z][a-z0-9._]*@[a-z0-9.-]+\.[a-z]{2,}(\.[a-z]{2,})?$/;

// Email validation
export const validateEmail = (email: string): boolean => {
  return EMAIL_PATTERN.test(email);
};

// Required field validation
export const validateRequired = (
  value: any,
  fieldName: string
): string | null => {
  if (!value || (typeof value === "string" && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Phone number validation (Indian format)
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone.replace(/\s/g, ""));
};

// Aadhar number validation
export const validateAadhar = (aadhar: string): boolean => {
  const aadharRegex = /^\d{4}\s\d{4}\s\d{4}$/;
  return aadharRegex.test(aadhar);
};

// Aadhar number validation with error message
export const validateAadharNumber = (aadhar: string): string | null => {
  if (!aadhar || aadhar.trim() === "") {
    return "Aadhar number is required";
  }
  if (!validateAadhar(aadhar)) {
    return "Aadhar number must be in format: XXXX XXXX XXXX (12 digits with spaces)";
  }
  return null;
};

// PAN number validation
export const validatePanNumber = (pan: string): boolean => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase());
};

// PAN number validation with error message
export const validatePanNumberWithError = (pan: string): string | null => {
  if (!pan || pan.trim() === "") {
    return "PAN Number is required";
  }
  if (!validatePanNumber(pan)) {
    return "PAN Number must be in format: ABCDE1234F (5 letters + 4 digits + 1 letter)";
  }
  return null;
};

// Pincode validation
export const validatePincode = (pincode: string): boolean => {
  const pincodeRegex = /^[1-9][0-9]{5}$/;
  return pincodeRegex.test(pincode);
};

export const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*.?&#])[A-Za-z\d@$!%*.?&#]{8,}$/;

// Password validation - UPDATED with complex regex pattern
export const validatePassword = (password: string): string | null => {
  if (!password || password.trim() === "") {
    return "Password is required";
  }

  // Complex password validation with regex
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*.?&#])[A-Za-z\d@$!%*.?&#]{8,}$/;

  if (!passwordRegex.test(password)) {
    return "Password must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character (@$!%*.?&#)";
  }

  return null;
};

// Complex password validation for registration (with all requirements)
export const validateComplexPassword = (password: string): ValidationResult => {
  const errors: string[] = [];

  if (!password || password.trim() === "") {
    errors.push("Password is required");
    return { isValid: false, errors };
  }

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/[@$!%*?&#]/.test(password)) {
    errors.push(
      "Password must contain at least one special character (@$!%*?&#)"
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Date validation
export const validateDate = (date: string | Date): boolean => {
  if (!date) return false;
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
};

// Age validation
export const validateAge = (
  birthDate: string | Date,
  minAge: number = 3,
  maxAge: number = 100
): boolean => {
  if (!birthDate) return false;

  const today = new Date();
  const birth = new Date(birthDate);
  const age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    return age - 1 >= minAge && age - 1 <= maxAge;
  }

  return age >= minAge && age <= maxAge;
};

// Roll number validation - UPDATED to return string | null
export const validateRollNumber = (rollNumber: string): string | null => {
  if (!rollNumber || rollNumber.trim() === "") {
    return "Roll Number is required";
  }
  if (!/^\d+$/.test(rollNumber)) {
    return "Roll Number must contain only numbers";
  }
  if (rollNumber.length < 1) {
    return "Roll Number must be at least 1 digit";
  }
  return null;
};

// Enrolment number validation - UPDATED to return string | null
export const validateEnrolmentNumber = (enrolmentNo: string): string | null => {
  if (!enrolmentNo || enrolmentNo.trim() === "") {
    return "Enrolment Number is required";
  }
  if (!/^\d+$/.test(enrolmentNo)) {
    return "Enrolment Number must contain only numbers";
  }
  if (enrolmentNo.length < 1) {
    return "Enrolment Number must be at least 1 digit";
  }
  return null;
};

// Percentage validation
export const validatePercentage = (percentage: number): boolean => {
  return percentage >= 0 && percentage <= 100;
};

// Marks validation
export const validateMarks = (
  marks: number,
  maxMarks: number = 100
): boolean => {
  return marks >= 0 && marks <= maxMarks;
};

// File size validation
export const validateFileSize = (
  file: File,
  maxSizeMB: number = 5
): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

// File type validation
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.includes(file.type);
};

// URL validation
export const validateURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Form validation helper
export const validateForm = (
  data: any,
  rules: Record<string, (value: any) => string | null>
): FieldValidation => {
  const errors: FieldValidation = {};

  Object.keys(rules).forEach((field) => {
    const error = rules[field](data[field]);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

// Check if form has errors
export const hasFormErrors = (errors: FieldValidation): boolean => {
  return Object.values(errors).some((error) => error !== null);
};

// Format validation error messages
export const formatValidationErrors = (errors: FieldValidation): string => {
  return Object.values(errors)
    .filter((error) => error !== null)
    .join("\n");
};

// Student form validation
export const validateStudentForm = (data: any): FieldValidation => {
  return validateForm(data, {
    firstName: (value) => validateRequired(value, "First Name"),
    lastName: (value) => validateRequired(value, "Last Name"),
    phone: (value) => {
      if (value && !validatePhone(value))
        return "Please enter a valid phone number";
      return null;
    },
    dateOfBirth: (value) => {
      const required = validateRequired(value, "Date of Birth");
      if (required) return required;
      if (!validateDate(value)) return "Please enter a valid date";
      if (!validateAge(value))
        return "Student age must be between 3 and 100 years";
      return null;
    },
    rollNumber: (value) => {
      const required = validateRequired(value, "Roll Number");
      if (required) return required;
      if (!validateRollNumber(value)) return "Please enter a valid roll number";
      return null;
    },
    enrolmentNo: (value) => {
      const required = validateRequired(value, "Enrolment Number");
      if (required) return required;
      if (!validateEnrolmentNumber(value))
        return "Please enter a valid enrolment number";
      return null;
    },
  });
};

// Helper: Capitalize first letter of each word
export function capitalizeWords(str: string): string {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

export const validateSchoolName = (name: string): string | null => {
  if (!name || name.trim() === "") {
    return "School Name is required";
  }
  // Remove leading/trailing spaces and replace multiple spaces with one
  const cleaned = name.trim().replace(/\s+/g, " ");
  // At least 3 letters (not counting spaces)
  const letterCount = cleaned.replace(/[^A-Za-z]/g, "").length;
  if (letterCount < 3) {
    return "School Name must contain at least 3 letters";
  }
  // Only one space between words
  if (/  +/.test(cleaned)) {
    return "Only one space is allowed between words in School Name";
  }
  // No multiple spaces anywhere
  if (/\s{2,}/.test(name)) {
    return "Only one space is allowed between words in School Name";
  }
  // Must start with a capital letter
  if (!/^[A-Z]/.test(cleaned)) {
    return "School Name must start with a capital letter";
  }
  // No leading/trailing spaces (already trimmed above)
  if (name !== cleaned) {
    return "School Name should not have leading or trailing spaces";
  }
  return null;
};

export const validateSchoolForm = (data: any): FieldValidation => {
  const errors: FieldValidation = {};

  // School Name custom validation
  const nameError = validateSchoolName(data.name);
  if (nameError) errors.name = nameError;

  // Required text fields
  if (!data.code || data.code.trim() === "")
    errors.code = "School Code is required";
  if (!data.address || data.address.trim() === "")
    errors.address = "Address is required";
  if (!data.city || data.city.trim() === "") errors.city = "City is required";
  if (!data.state || data.state.trim() === "")
    errors.state = "State is required";
  if (!data.zipCode || data.zipCode.trim() === "")
    errors.zipCode = "Zip Code is required";
  if (!data.country || data.country.trim() === "")
    errors.country = "Country is required";
  // if (!data.diseCode || data.diseCode.trim() === "") // Commented out
  //   errors.diseCode = "DISE Code is required";
  // if (!data.block || data.block.trim() === "") // Commented out
  //   errors.block = "Block is required";
  if (!data.district || data.district.trim() === "")
    errors.district = "District is required";
  // if (!data.pinNo || data.pinNo.trim() === "") // Commented out
  //   errors.pinNo = "Pin No is required";
  if (!data.principalName || data.principalName.trim() === "")
    errors.principalName = "Principal Name is required";
  // if (!data.classStaffName || data.classStaffName.trim() === "") // Commented out
  //   errors.classStaffName = "Class Staff Name is required";
  if (!data.medium || data.medium.trim() === "")
    errors.medium = "Medium is required";
  // if (!data.academicYear || data.academicYear.trim() === "") // Commented out
  //   errors.academicYear = "Academic Year is required";

  if (!data.principalEmail || data.principalEmail.trim() === "") {
    errors.principalEmail = "Principal Email is required";
  } else if (!validateEmail(data.principalEmail)) {
    errors.principalEmail = "Please enter a valid principal email address";
  }
  // if (!data.teacherEmail || data.teacherEmail.trim() === "") { // Commented out
  //   errors.teacherEmail = "Teacher Email is required";
  // } else if (!validateEmail(data.teacherEmail)) {
  //   errors.teacherEmail = "Please enter a valid teacher email address";
  // }

  // Phone fields
  if (!data.phone || data.phone.trim() === "") {
    errors.phone = "Phone number is required";
  } else if (!validatePhone(data.phone)) {
    errors.phone = "Please enter a valid phone number";
  }
  if (!data.principalMobile || data.principalMobile.toString().trim() === "") {
    errors.principalMobile = "Principal Mobile is required";
  } else if (!validatePhone(data.principalMobile.toString())) {
    errors.principalMobile = "Please enter a valid principal mobile number";
  }
  // if (!data.teacherMobile || data.teacherMobile.toString().trim() === "") { // Commented out
  //   errors.teacherMobile = "Teacher Mobile is required";
  // } else if (!validatePhone(data.teacherMobile.toString())) {
  //   errors.teacherMobile = "Please enter a valid teacher mobile number";
  // }

  // Number fields - Commented out
  // if (
  //   data.scMathsStudents === undefined ||
  //   data.scMathsStudents === null ||
  //   data.scMathsStudents === ""
  // ) {
  //   errors.scMathsStudents = "SC Maths Students is required";
  // }
  // if (
  //   data.dapdMathsStudents === undefined ||
  //   data.dapdMathsStudents === null ||
  //   data.dapdMathsStudents === ""
  // ) {
  //   errors.dapdMathsStudents = "DAPD Maths Students is required";
  // }
  // if (
  //   data.totalStudents === undefined ||
  //   data.totalStudents === null ||
  //   data.totalStudents === ""
  // ) {
  //   errors.totalStudents = "Total Students is required";
  // }

  return errors;
};

// CODE: Only capital letters, numbers, and -,/ allowed
export const validateSchoolCode = (code: string): string | null => {
  if (!code || code.trim() === "") return "School Code is required";
  if (!/^[A-Z0-9\-\/]+$/.test(code))
    return "Only capital letters, numbers, and -,/ are allowed";
  return null;
};

// ADDRESS: 5-300 chars, only -, , and / as special chars
export const validateSchoolAddress = (address: string): string | null => {
  if (!address || address.trim() === "") return "Address is required";
  if (address.length < 5) return "Address must be at least 5 characters";
  if (address.length > 300) return "Address must be at most 300 characters";
  if (!/^[A-Za-z0-9\-\/, ]+$/.test(address))
    return "Only letters, numbers, spaces, -, /, and , are allowed";
  return null;
};

// CITY: 3-50 letters, only letters and spaces
export const validateSchoolCity = (city: string): string | null => {
  if (!city || city.trim() === "") return "City is required";
  const letterCount = city.replace(/[^A-Za-z]/g, "").length;
  if (letterCount < 3) return "City must be at least 3 letters";
  if (letterCount > 50) return "City must be at most 50 letters";
  if (!/^[A-Za-z ]+$/.test(city))
    return "Only letters and spaces are allowed in City";
  return null;
};

// STATE: 3-50 letters, only letters and spaces
export const validateSchoolState = (state: string): string | null => {
  if (!state || state.trim() === "") return "State is required";
  const letterCount = state.replace(/[^A-Za-z]/g, "").length;
  if (letterCount < 3) return "State must be at least 3 letters";
  if (letterCount > 50) return "State must be at most 50 letters";
  if (!/^[A-Za-z ]+$/.test(state))
    return "Only letters and spaces are allowed in State";
  return null;
};

// ZIPCODE: Only numbers, no other chars
export const validateSchoolZipCode = (zip: string): string | null => {
  if (!zip || zip.trim() === "") return "Zip Code is required";
  if (!/^[0-9]+$/.test(zip)) return "Only numbers are allowed in Zip Code";
  return null;
};

// COUNTRY: 3-50 letters, only letters and spaces
export const validateSchoolCountry = (country: string): string | null => {
  if (!country || country.trim() === "") return "Country is required";
  const letterCount = country.replace(/[^A-Za-z]/g, "").length;
  if (letterCount < 3) return "Country must be at least 3 letters";
  if (letterCount > 50) return "Country must be at most 50 letters";
  if (!/^[A-Za-z ]+$/.test(country))
    return "Only letters and spaces are allowed in Country";
  return null;
};

// PHONE: Only numbers, exactly 10 digits
export const validateSchoolPhone = (phone: string): string | null => {
  if (!phone || phone.trim() === "") return "Phone number is required";
  if (!/^[0-9]{10}$/.test(phone))
    return "Phone number must be exactly 10 digits";
  return null;
};

// DISE CODE: Only numbers, no special chars or letters - Commented out
// export const validateSchoolDiseCode = (dise: string): string | null => {
//   if (!dise || dise.trim() === "") return "DISE Code is required";
//   if (!/^[0-9]+$/.test(dise)) return "Only numbers are allowed in DISE Code";
//   return null;
// };

// DISTRICT: Only letters, min 3, max 50
export const validateSchoolDistrict = (district: string): string | null => {
  if (!district || district.trim() === "") return "District is required";
  if (!/^[A-Za-z]+$/.test(district))
    return "Only letters are allowed in District";
  if (district.length < 3) return "District must be at least 3 letters";
  if (district.length > 50) return "District must be at most 50 letters";
  return null;
};

// User form validation - FIX the password validation call
export const validateUserForm = (
  data: any,
  isEdit: boolean = false
): FieldValidation => {
  const errors: FieldValidation = {};

  // Username validation - removed all validation, only check if not empty
  if (!data.username || data.username.trim() === "") {
    errors.username = "Username is required";
  }
  // No other username validation applied

  // Password validation for new users only
  if (!isEdit) {
    if (!data.password || data.password.trim() === "") {
      errors.password = "Password is required";
    } else {
      const passwordError = validatePassword(data.password);
      if (passwordError) {
        errors.password = passwordError;
      }
    }
  }

  // Email validation
  if (data.email && !validateEmail(data.email)) {
    errors.email = "Please enter a valid email address";
  }

  // DO NOT add phone or roles validation here

  return errors;
};

// Fee form validation
export const validateFeeForm = (data: any): FieldValidation => {
  return validateForm(data, {
    amount: (value) => {
      const required = validateRequired(value, "Amount");
      if (required) return required;
      if (isNaN(value) || value <= 0) return "Amount must be a positive number";
      return null;
    },
    dueDate: (value) => {
      const required = validateRequired(value, "Due Date");
      if (required) return required;
      if (!validateDate(value)) return "Please enter a valid date";
      return null;
    },
    feeType: (value) => validateRequired(value, "Fee Type"),
    studentId: (value) => validateRequired(value, "Student"),
  });
};

// Attendance form validation
export const validateAttendanceForm = (data: any): FieldValidation => {
  return validateForm(data, {
    studentId: (value) => validateRequired(value, "Student"),
    date: (value) => {
      const required = validateRequired(value, "Date");
      if (required) return required;
      if (!validateDate(value)) return "Please enter a valid date";
      return null;
    },
    status: (value) => validateRequired(value, "Status"),
  });
};

// Book form validation
export const validateBookForm = (data: any): FieldValidation => {
  return validateForm(data, {
    title: (value) => validateRequired(value, "Title"),
    author: (value) => validateRequired(value, "Author"),
    isbn: (value) => validateRequired(value, "ISBN"),
    publicationYear: (value) => {
      const required = validateRequired(value, "Publication Year");
      if (required) return required;
      if (isNaN(value) || value < 1000 || value > new Date().getFullYear())
        return "Enter a valid publication year";
      return null;
    },
    category: (value) => validateRequired(value, "Category"),
    quantity: (value) => {
      const required = validateRequired(value, "Quantity");
      if (required) return required;
      if (isNaN(value) || value < 0)
        return "Quantity must be a non-negative number";
      return null;
    },
    totalCopies: (value) => {
      const required = validateRequired(value, "Total Copies");
      if (required) return required;
      if (isNaN(value) || value < 0)
        return "Total copies must be a non-negative number";
      return null;
    },
    availableCopies: (value) => {
      const required = validateRequired(value, "Available Copies");
      if (required) return required;
      if (isNaN(value) || value < 0)
        return "Available copies must be a non-negative number";
      if (value > data.totalCopies)
        return "Available copies cannot exceed total copies";
      return null;
    },
    publisher: (_value) => null,
    description: (_value) => null,
    location: (_value) => null,
    status: (_value) => null,
  });
};

// Class form validation
export const validateClassForm = (data: any): FieldValidation => {
  return validateForm(data, {
    name: (value) => validateRequired(value, "Class Name"),
    section: (value) => validateRequired(value, "Section"),
  });
};

// Fee receipt form validation
export const validateFeeReceiptForm = (data: any): FieldValidation => {
  return validateForm(data, {
    studentId: (value) => validateRequired(value, "Student ID"),
    amount: (value) => {
      const required = validateRequired(value, "Amount");
      if (required) return required;
      if (isNaN(value) || value <= 0) return "Amount must be a positive number";
      return null;
    },
    date: (value) => {
      const required = validateRequired(value, "Date");
      if (required) return required;
      if (!validateDate(value)) return "Please enter a valid date";
      return null;
    },
    status: (value) => validateRequired(value, "Status"),
  });
};

// Fee record form validation
export const validateFeeRecordForm = (data: any): FieldValidation => {
  return validateForm(data, {
    feeType: (value) => validateRequired(value, "Fee Type"),
    amount: (value) => {
      const required = validateRequired(value, "Amount");
      if (required) return required;
      if (isNaN(value) || value <= 0) return "Amount must be a positive number";
      return null;
    },
    dueDate: (value) => {
      const required = validateRequired(value, "Due Date");
      if (required) return required;
      if (!validateDate(value)) return "Please enter a valid due date";
      return null;
    },
    paymentMethod: (value) => validateRequired(value, "Payment Method"),
    transactionId: (value) => {
      if (data.paymentMethod && data.paymentMethod !== "Cash") {
        return validateRequired(value, "Transaction ID");
      }
      return null;
    },
    status: (value) => validateRequired(value, "Status"),
    studentId: (value) => validateRequired(value, "Student"),
    paidDate: (_value) => null,
  });
};

// Gosvara sheet form validation
export const validateGosvaraSheetForm = (data: any): FieldValidation => {
  const errors: FieldValidation = {};
  if (!data.studentId) errors.studentId = "Student is required";
  if (!data.marksheetType) errors.marksheetType = "Marksheet type is required";
  if (!data.monthType) errors.monthType = "Month is required";
  if (!data.subjectMarks || data.subjectMarks.length === 0)
    errors.subjectMarks = "At least one subject is required";
  if (Array.isArray(data.subjectMarks)) {
    data.subjectMarks.forEach((sm: any, idx: number) => {
      if (sm.marks < 0 || sm.marks > 100)
        errors[`subject_${idx}_marks`] = "Marks must be 0-100";
    });
  }
  return errors;
};

// Library transaction form validation
export const validateLibraryTransactionForm = (data: any): FieldValidation => {
  const errors: FieldValidation = {};
  if (!data.bookId) errors.bookId = "Book is required";
  if (!data.studentId) errors.studentId = "Student is required";
  if (!data.issueDate) errors.issueDate = "Issue date is required";
  if (!data.dueDate) errors.dueDate = "Due date is required";
  if (!data.status) errors.status = "Status is required";
  if (data.fineAmount == null || isNaN(data.fineAmount) || data.fineAmount < 0)
    errors.fineAmount = "Fine amount must be 0 or greater";
  return errors;
};

// Marksheet form validation
// Note: This function uses default max marks (75:25). For vocational subjects (40:60),
// validation should be done in the component using getCustomMaxMarks helper function.
export const validateMarkSheetForm = (data: any, vocationalSubjects?: Set<string>): FieldValidation => {
  const errors: FieldValidation = {};
  if (!data.studentId) errors.studentId = "Student is required";
  if (!data.marksheetType) errors.marksheetType = "Marksheet type is required";
  if (!data.subjects || data.subjects.length === 0)
    errors.subjects = "At least one subject is required";
  if (Array.isArray(data.subjects)) {
    data.subjects.forEach((sm: any, idx: number) => {
      // Determine max marks based on vocational status
      const normalizedSubjectName = (sm.subjectName || sm.subject || '').toLowerCase().trim();
      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
      const maxTheory = isVocationalSubject ? 40 : 75;
      const maxPractical = isVocationalSubject ? 60 : 25;
      
      if (sm.theoryMarks < 0 || sm.theoryMarks > maxTheory)
        errors[`subject_${idx}_theoryMarks`] = `Theory marks must be 0-${maxTheory}`;
      if (sm.practicalMarks < 0 || sm.practicalMarks > maxPractical)
        errors[`subject_${idx}_practicalMarks`] =
          `Practical marks must be 0-${maxPractical}`;
    });
  }
  return errors;
};

// Result form validation
export const validateResultForm = (data: any): FieldValidation => {
  return validateForm(data, {
    studentId: (value) => validateRequired(value, "Student ID"),
    subject: (value) => validateRequired(value, "Subject"),
    marks: (value) => {
      const required = validateRequired(value, "Marks");
      if (required) return required;
      if (isNaN(value) || value < 0 || value > 100)
        return "Marks must be between 0 and 100";
      return null;
    },
    grade: (value) => validateRequired(value, "Grade"),
    term: (value) => validateRequired(value, "Term"),
    year: (value) => validateRequired(value, "Year"),
  });
};

// ACADEMIC YEAR: Only numbers and dash, format like 2020-2021 - Commented out
// export const validateAcademicYear = (year: string): string | null => {
//   if (!year || year.trim() === "") return "Academic Year is required";
//   if (!/^\d{4}-\d{4}$/.test(year))
//     return "Academic Year must be in format YYYY-YYYY (e.g., 2020-2021)";
//   return null;
// };

// USERNAME: Validation removed - allow any username
export const validateUsername = (username: string): string | null => {
  if (!username) return "Username is required";
  
  // All other validations removed - allow any characters and any length
  return null;
};

// FULL NAME: Up to 5 words, each at least 1 letter, only one space between words, no leading/trailing spaces, only letters
// export const validateFullName = (fullName: string): string | null => {
//   if (!fullName) return "Full Name is required";

//   // Check for leading spaces
//   if (fullName.startsWith(" ")) {
//     return "Full Name should not start with a space";
//   }

//   // Check for trailing spaces
//   if (fullName.endsWith(" ")) {
//     return "Full Name should not end with a space";
//   }

//   // Check for multiple consecutive spaces
//   if (/\s{2,}/.test(fullName)) {
//     return "Full Name should not have multiple consecutive spaces";
//   }

//   // Check if contains only letters and spaces
//   if (!/^[A-Za-z\s]+$/.test(fullName)) {
//     return "Full Name should contain only letters and spaces";
//   }

//   // Check word count (up to 5 words)
//   const words = fullName.trim().split(/\s+/);
//   if (words.length > 5) {
//     return "Full Name should not exceed 5 words";
//   }

//   // Check each word has at least 1 letter
//   for (const word of words) {
//     if (word.length < 1) {
//       return "Each word in Full Name should have at least 1 letter";
//     }
//   }

//   return null;
// };

// Application Form validation - Simplified to only validate required fields
export const validateApplicationForm = (data: any): FieldValidation => {
  const errors: FieldValidation = {};

  // Only validate the specified required fields
  if (!data.studentName || data.studentName.trim() === "")
    errors.studentName = "Student Name is required";

  if (!data.fatherName || data.fatherName.trim() === "")
    errors.fatherName = "Father's Name is required";

  if (!data.motherName || data.motherName.trim() === "")
    errors.motherName = "Mother's Name is required";

  if (!data.gender || data.gender.trim() === "")
    errors.gender = "Gender is required";

  // Date validations
  if (!data.dateOfBirth || data.dateOfBirth.trim() === "") {
    errors.dateOfBirth = "Date of Birth is required";
  }

  if (!data.admissionDate || data.admissionDate.trim() === "") {
    errors.admissionDate = "Admission Date is required";
  }

  return errors;
};

// Individual field validators for real-time validation
export const validateApplicationNo = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Application Number is required";
  }
  if (value.length < 3) {
    return "Application Number must be at least 3 characters";
  }
  return null;
};

export const validateAddress = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Address is required";
  }
  if (value.length < 5) {
    return "Address must be at least 5 characters";
  }
  return null;
};

export const validateCity = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "City is required";
  }
  if (!/^[A-Za-z\s]+$/.test(value)) {
    return "City must contain only letters and spaces";
  }
  return null;
};

export const validateState = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "State is required";
  }
  if (!/^[A-Za-z\s]+$/.test(value)) {
    return "State must contain only letters and spaces";
  }
  return null;
};

export const validatePinCode = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "PIN Code is required";
  }
  if (!/^\d{6}$/.test(value)) {
    return "PIN Code must be exactly 6 digits";
  }
  return null;
};

export const validatePhoneNumber = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Phone Number is required";
  }
  if (!/^\d{10}$/.test(value)) {
    return "Phone Number must be exactly 10 digits";
  }
  return null;
};

export const validateBankDetails = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Bank details are required";
  }
  if (value.length < 2) {
    return "Bank details must be at least 2 characters";
  }
  return null;
};

export const validateIFSCCode = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "IFSC Code is required";
  }
  // More flexible IFSC validation - allow 9-11 characters
  if (!/^[A-Z]{4}[0-9A-Z]{5,7}$/.test(value.toUpperCase())) {
    return "IFSC Code must be 9-11 characters in format: ABCD0123456";
  }
  return null;
};

export const validateAccountNumber = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Account Number is required";
  }
  if (!/^\d{9,18}$/.test(value)) {
    return "Account Number must be 9-18 digits";
  }
  return null;
};

export const validateAnnualIncome = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Annual Income is required";
  }
  if (!/^\d+$/.test(value)) {
    return "Annual Income must contain only numbers";
  }
  if (parseInt(value) < 0) {
    return "Annual Income cannot be negative";
  }
  return null;
};

export const validateVillage = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Village is required";
  }
  if (!/^[A-Za-z\s]+$/.test(value)) {
    return "Village must contain only letters and spaces";
  }
  if (value.length < 2) {
    return "Village must be at least 2 characters";
  }
  return null;
};

export const validateStreet = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Street is required";
  }
  if (value.length < 2) {
    return "Street must be at least 2 characters";
  }
  return null;
};

export const validateLandmark = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Landmark is required";
  }
  if (value.length < 2) {
    return "Landmark must be at least 2 characters";
  }
  return null;
};

export const validateArea = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Area is required";
  }
  if (value.length < 2) {
    return "Area must be at least 2 characters";
  }
  return null;
};

export const validatePostOffice = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "Post Office is required";
  }
  if (value.length < 2) {
    return "Post Office must be at least 2 characters";
  }
  return null;
};

export const validateDistrict = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return "District is required";
  }
  if (!/^[A-Za-z\s]+$/.test(value)) {
    return "District must contain only letters and spaces";
  }
  if (value.length < 2) {
    return "District must be at least 2 characters";
  }
  return null;
};
